import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { CompassIcon, TargetIcon, BrainCircuitIcon, CrownIcon, ClockIcon, UsersIcon, CheckCircleIcon } from 'lucide-react';
type Course = {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  duration: string;
  students: string;
  level: string;
  features: string[];
  price: string;
  popular?: boolean;
};
const courses: Course[] = [{
  id: 'meridian',
  name: 'Meridian Protocol',
  subtitle: 'Fundamenty analizy technicznej',
  description: 'Solidne podstawy czytania wykresów, rozpoznawania formacji i zrozumienia dynamiki rynku.',
  icon: <CompassIcon className="w-6 h-6" data-id="element-25" />,
  duration: '32 godziny',
  students: '1,247',
  level: 'Początkujący',
  features: ['Świece japońskie i formacje', 'Wsparcia i opory', 'Podstawy wolumenu', 'Zarządzanie pozycją'],
  price: '1,497 PLN'
}, {
  id: 'apex',
  name: 'Apex Convergence',
  subtitle: 'Zaawansowane rozpoznawanie wzorców',
  description: 'Głęboka analiza struktur rynkowych, harmonicznych i zaawansowanych technik wejścia.',
  icon: <TargetIcon className="w-6 h-6" data-id="element-26" />,
  duration: '48 godzin',
  students: '892',
  level: 'Średniozaawansowany',
  features: ['Formacje harmoniczne', 'Analiza wielointerwałowa', 'Wyckoff & Smart Money', 'Zaawansowany price action'],
  price: '2,497 PLN',
  popular: true
}, {
  id: 'cipher',
  name: 'Cipher Matrix',
  subtitle: 'Podstawy tradingu algorytmicznego',
  description: 'Wprowadzenie do automatyzacji strategii i wykorzystania danych w procesie decyzyjnym.',
  icon: <BrainCircuitIcon className="w-6 h-6" data-id="element-27" />,
  duration: '40 godzin',
  students: '534',
  level: 'Zaawansowany',
  features: ['Pine Script fundamentals', 'Backtesting strategii', 'Optymalizacja parametrów', 'Integracja z API'],
  price: '3,297 PLN'
}, {
  id: 'zenith',
  name: 'Zenith Mastery',
  subtitle: 'Kompletny system tradingowy',
  description: 'Pełny program mentorski. Budowa własnego systemu od podstaw do profesjonalnego poziomu.',
  icon: <CrownIcon className="w-6 h-6" data-id="element-28" />,
  duration: '120 godzin',
  students: '312',
  level: 'Wszystkie poziomy',
  features: ['Wszystkie moduły programu', 'Indywidualne sesje 1:1', 'Dostęp do społeczności', 'Dożywotnie aktualizacje'],
  price: '7,997 PLN'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function CoursesSection() {
  return <section id="courses" className="py-24 bg-gray-950" data-id="element-29">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-30">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-31">
          <motion.span initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-emerald-400 text-sm font-medium tracking-wider uppercase" data-id="element-32">
            Programy szkoleniowe
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-4" data-id="element-33">
            Wybierz swoją ścieżkę
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-gray-400 max-w-2xl mx-auto" data-id="element-34">
            Każdy program został zaprojektowany z myślą o praktycznych
            umiejętnościach, które możesz natychmiast zastosować na rynku.
          </motion.p>
        </div>

        {/* Courses Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-35">
          {courses.map(course => <motion.div key={course.id} variants={itemVariants} data-id="element-36">
              <Card hover glow={course.popular ? 'bullish' : 'none'} className={`p-6 h-full ${course.popular ? 'border-emerald-500/30' : ''}`} data-id="element-37">
                {course.popular && <div className="absolute -top-3 left-6 px-3 py-1 bg-emerald-500 text-gray-900 text-xs font-semibold rounded-full" data-id="element-38">
                    Najpopularniejszy
                  </div>}

                <div className="flex items-start gap-4 mb-4" data-id="element-39">
                  <div className={`p-3 rounded-xl ${course.popular ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-800 text-gray-400'}`} data-id="element-40">
                    {course.icon}
                  </div>
                  <div className="flex-1" data-id="element-41">
                    <h3 className="text-xl font-bold text-white" data-id="element-42">
                      {course.name}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-43">{course.subtitle}</p>
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-6" data-id="element-44">
                  {course.description}
                </p>

                {/* Meta */}
                <div className="flex items-center gap-4 mb-6 text-sm" data-id="element-45">
                  <span className="flex items-center gap-1.5 text-gray-500" data-id="element-46">
                    <ClockIcon className="w-4 h-4" data-id="element-47" />
                    {course.duration}
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-500" data-id="element-48">
                    <UsersIcon className="w-4 h-4" data-id="element-49" />
                    {course.students} uczestników
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6" data-id="element-50">
                  {course.features.map((feature, index) => <li key={index} className="flex items-center gap-2 text-sm text-gray-300" data-id="element-51">
                      <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-52" />
                      {feature}
                    </li>)}
                </ul>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-800" data-id="element-53">
                  <div data-id="element-54">
                    <span className="text-2xl font-bold text-white font-mono" data-id="element-55">
                      {course.price}
                    </span>
                  </div>
                  <Button variant={course.popular ? 'primary' : 'secondary'} size="sm" data-id="element-56">
                    Dowiedz się więcej
                  </Button>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}